import { FunctionComponent, useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import { ConfigContext, UserContext } from '../../context';
import { useStoriesByChannelIdQuery, useStoryByIdQuery } from '../../services';
import {
  CreateCardWithInput,
  StoryFeedItemsHeader,
  useAnalytics,
  useBeforeUnload,
  useDuration,
} from '../../shared';
import { NoContent } from '../Error';
import { StoryFeed } from '../StoryFeedItems';

import classes from './PinnedStoryFeedItems.module.scss';

export const PinnedStoryFeedItems: FunctionComponent = () => {
  const { t } = useTranslation();

  const { logClickOpenStory } = useAnalytics();

  const { startTrackTime, getDuration } = useDuration();

  const storyId = Number(useParams().storyId);

  const { data: story, isFetching } = useStoryByIdQuery({ storyId });

  const { channelId } = useContext(UserContext).userInfo.userData;

  const { showOnPinnedStoryFeed, addContentLocaliseKey } =
    useContext(ConfigContext).config.elements.addContent;

  const { data: postInStories, isLoading: isPostInStoriesLoading } = useStoriesByChannelIdQuery(
    { channelId, filter: { onlyPostIn: true } },
    { skip: !showOnPinnedStoryFeed }
  );

  const canPostInCurrentStory = useMemo(
    () => Boolean(postInStories?.some(({ id }) => id === storyId)),
    [postInStories, storyId]
  );

  const renderCreateCardInput = useMemo(() => {
    if (isPostInStoriesLoading) {
      return <Skeleton height={'4rem'} />;
    }

    if (!canPostInCurrentStory) {
      return null;
    }

    return <CreateCardWithInput label={t(addContentLocaliseKey)} />;
  }, [isPostInStoriesLoading, canPostInCurrentStory, t, addContentLocaliseKey]);

  const logClickOpen = useCallback(
    () =>
      logClickOpenStory({
        channel_id: channelId,
        story_id: storyId,
        mix_id: storyId,
        time_stamp: new Date().toISOString(),
        duration: getDuration(),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channelId, logClickOpenStory, storyId]
  );

  useEffect(() => {
    startTrackTime();
    return () => logClickOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logClickOpen]);

  useBeforeUnload(logClickOpen);

  if (isFetching) {
    return <Skeleton height={'10rem'} />;
  }

  if (!story) {
    return <NoContent />;
  }

  return (
    <div className={classes['story-feed-items']}>
      <StoryFeedItemsHeader story={story} />
      {renderCreateCardInput}
      <StoryFeed storyId={Number(storyId)} />
    </div>
  );
};
